import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Autocomplete,
  IconButton,
  Grid,
  Fade,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

function ReportAdmin() {  
  const { t } = useAdminLanguage();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingReport, setEditingReport] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isLimited, setIsLimited] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // Récupérer tous les PV et utilisateurs au chargement
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const [reportsResponse, usersResponse] = await Promise.all([
          axios.get('/api/report', config),
          axios.get('/api/user', config),
        ]);

        setReports(reportsResponse.data);
        setFilteredReports(reportsResponse.data);
        setUsers(usersResponse.data);
        setLoading(false);
      } catch (err) {
        setError('Erreur lors du chargement des données.');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtrer les PV en fonction de la recherche
  useEffect(() => {
    const filtered = reports.filter((report) =>
      `${report.number}/${report.year}`.includes(searchTerm) ||
      report.indice.includes(searchTerm) ||
      report.createdBy.includes(searchTerm)
    );
    setFilteredReports(filtered);
  }, [searchTerm, reports]);

  // Ouvrir la fenêtre de confirmation de suppression
  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteConfirmOpen(true);
  };

  // Confirmer la suppression d'un PV
  const handleDeleteConfirm = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      await axios.delete(`/api/report/${reportToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(reports.filter((r) => r._id !== reportToDelete._id));
      setFilteredReports(filteredReports.filter((r) => r._id !== reportToDelete._id));
      setDeleteConfirmOpen(false);
      setReportToDelete(null);
    } catch (err) {
      setError('Erreur lors de la suppression du PV.');
      setDeleteConfirmOpen(false);
      setReportToDelete(null);
    }
  };

  // Annuler la suppression
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setReportToDelete(null);
  };

  // Supprimer un PV
  const handleDelete = async (reportId) => {
    try {
      const token = sessionStorage.getItem('authToken');
      await axios.delete(`/api/report/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(reports.filter((r) => r._id !== reportId));
      setFilteredReports(filteredReports.filter((r) => r._id !== reportId));
    } catch (err) {
      setError('Erreur lors de la suppression du PV.');
    }
  };

  // Démarrer l'édition d'un PV
  const handleEdit = (report) => {
    setEditingReport(report._id);
    setIsLimited(report.access.type === 'restricted');
    setSelectedUsers(users.filter((u) => report.access.users.includes(u.matricule)));
  };

  // Sauvegarder les modifications
  const handleSave = async (reportId) => {
    try {
      const token = sessionStorage.getItem('authToken');
      const updatedData = {
        access: {
          type: isLimited ? 'restricted' : 'public',
          users: isLimited ? selectedUsers.map((u) => u.matricule) : [],
        },
      };
      const response = await axios.put(
        `/api/report/${reportId}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedReport = response.data;
      setReports(reports.map((r) => (r._id === reportId ? updatedReport : r)));
      setFilteredReports(filteredReports.map((r) => (r._id === reportId ? updatedReport : r)));
      setEditingReport(null);
      setSelectedUsers([]);
      setIsLimited(false);
    } catch (err) {
      setError('Erreur lors de la mise à jour du PV.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des procès-verbaux')}
          </Typography>

          <Info type="report" />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('Rechercher un procès-verbal')}
                    placeholder={t('Numéro, année, indice ou créateur')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 3 }}
                  />
                </Grid>
              </Grid>

              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('Indice')}</TableCell>
                      <TableCell>{t('Numéro')}</TableCell>
                      <TableCell>{t('Année')}</TableCell>
                      <TableCell>{t('Créateur')}</TableCell>
                      <TableCell>{t('Date de création')}</TableCell>
                      <TableCell>{t('Accès')}</TableCell>
                      <TableCell>{t('Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReports.map((report) => (
                      <TableRow key={report._id}>
                        <TableCell>{report.indice}</TableCell>
                        <TableCell>{report.number}</TableCell>
                        <TableCell>{report.year}</TableCell>
                        <TableCell>{report.createdBy}</TableCell>
                        <TableCell>{new Date(report.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {editingReport === report._id ? (
                            <Box>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isLimited}
                                    onChange={(e) => setIsLimited(e.target.checked)}
                                    color="primary"
                                  />
                                }
                                label={t('Accès restreint')}
                              />
                              {isLimited && (
                                <Autocomplete
                                  multiple
                                  options={users}
                                  getOptionLabel={(option) =>
                                    `${option.lastname.toUpperCase()} ${option.firstname} (${option.rank})`
                                  }
                                  value={selectedUsers}
                                  onChange={(e, newValue) => setSelectedUsers(newValue)}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t('Utilisateurs autorisés')}
                                      variant="outlined"
                                    />
                                  )}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography
                              color={report.access.type === 'restricted' ? 'primary' : 'text.secondary'}
                            >
                              {report.access.type === 'restricted'
                                ? t('Restreint ({count} utilisateurs)', { count: report.access.users.length })
                                : t('Non restreint')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                            {editingReport === report._id ? (
                              <IconButton
                                size="small"
                                onClick={() => handleSave(report._id)}
                                color="primary"
                                sx={{ '&:hover': { backgroundColor: 'primary.light' } }}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(report)}
                                  color="primary"
                                  sx={{ '&:hover': { backgroundColor: 'primary.light' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(report)}
                                  color="error"
                                  sx={{ '&:hover': { backgroundColor: 'error.light' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Modal de confirmation de suppression */}
              <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
              >
                <DialogTitle id="delete-dialog-title">
                  Confirmer la suppression
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="delete-dialog-description">
                    Êtes-vous sûr de vouloir supprimer le procès-verbal{' '}
                    <strong>
                      {reportToDelete?.indice} {reportToDelete?.number}/{reportToDelete?.year}
                    </strong>{' '}
                    ? Cette action est irréversible.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDeleteCancel} color="primary">
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleDeleteConfirm} 
                    color="error" 
                    variant="contained"
                    autoFocus
                  >
                    Supprimer
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      </Fade>
    </Container>
  );
}

export default ReportAdmin;